<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  shallowReactive,
  toRefs,
  useTemplateRef,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api';
import { Feature } from '@/api/tracking';
import {
  EMPTY_KEYWORD_FEED_MESSAGE,
  INITIAL_VISIBLE_DOCUMENT_COUNT,
  THEME_RELATED_NEWS_MAX_COUNT,
} from '@/constants';
import { DgrLoading } from '@stockmarkteam/donguri-ui';
import dayjs, { Dayjs } from 'dayjs';
import ThemeEmptyFeed from '@/components/common/theme-empty-feed.vue';
import ThemeEmptyState from '@/components/theme/theme-empty-state.vue';
import {
  AdpDocument,
  Article,
  ArticleMarkSource,
  DateArticles,
  FeedData,
  FeedType,
} from '@/types';
import { syncAdpDocumentActions } from '@/utils/adpDocument';
import { formatDateForFeedLabel } from '@/utils/formatters';
import { STATES, useTheme, useUserInfo } from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';
import PastArticles from '../theme/past-articles.vue';
import FeedArticlesDay from './feed-articles-day.vue';
import FeedDateSelectbox from './feed-date-selectbox.vue';

const props = withDefaults(
  defineProps<{
    feed: 'keyword_feed' | 'related_feed';
    hasViewMoreButton?: boolean;
  }>(),
  {
    hasViewMoreButton: true,
  },
);
const store = useStore();
const route = useRoute();
const emitter = useEmitter();

const { feed, hasViewMoreButton } = toRefs(props);

const feedApi = computed<
  (
    feedType: FeedType,
    date: Dayjs,
    page: number,
    limit: number,
  ) => Promise<{ articles: Article[]; count?: number }>
>(() =>
  feed.value === 'keyword_feed'
    ? (feedType: FeedType, date: Dayjs, page: number, limit: number) =>
        api.getThemeKeywordFeeds(themeId.value, feedType, date, page, limit)
    : (feedType: FeedType, date: Dayjs, page: number, limit: number) =>
        api.getThemeRelatedFeeds(themeId.value, feedType, date, page, limit),
);
const themeId = computed(() => Number(route.params.themeId));
const {
  data: themeResponse,
  state,
  mutate: mutateTheme,
} = useTheme(themeId.value);

watch(state, newState => {
  if (newState === STATES.SUCCESS) {
    initialize();
  }
});
const theme = computed(() => themeResponse.value?.themes);

const feedType = computed(() => store.state.feedType.feedType);

const { data: userInfo } = useUserInfo();

const hasFeed = computed(() => {
  const hasBothFeeds = feedType.value === 'all';
  const hasDomesticFeed =
    feedType.value === 'domestic' && !theme.value?.is_chinese;
  const hasForeignFeed =
    feedType.value === 'foreign' && theme.value?.is_foreign_media_enabled;
  return hasBothFeeds || hasDomesticFeed || hasForeignFeed;
});

const enableToEditTheme = computed(
  () =>
    userInfo.value?.role !== 'viewer' ||
    (theme.value?.access_scope === 'personal' &&
      theme.value?.user_id === userInfo.value?.id),
);

const feedLastUpdatedAt = computed(() =>
  theme.value?.feed_last_updated_at
    ? dayjs(theme.value.feed_last_updated_at)
    : dayjs(),
);

const isLoadingInitial = ref(true);

const currentDayFeedCount = ref(0);

const feedDayCount = ref(0);

const getFeedData = (
  feedType: 'domestic' | 'foreign',
  date: Dayjs,
): FeedData => {
  const initialArticleCount =
    feed.value === 'related_feed'
      ? THEME_RELATED_NEWS_MAX_COUNT
      : INITIAL_VISIBLE_DOCUMENT_COUNT;
  const load = async (articleCount = initialArticleCount) => {
    const data = articlesByDate.find(d => d.date === date)?.[feedType];
    if (!data || !data.value) return;

    data.value.isViewMoreButtonVisible = false;
    data.value.isFetchingData = true;
    const response = await feedApi.value(feedType, date, 1, articleCount);
    data.value.articles = response.articles;
    if (feed.value === 'keyword_feed') {
      const keyword_counts = theme.value?.keyword_feed_counts
        ? theme.value?.keyword_feed_counts[date.format('YYYY-MM-DD')]
        : { domestic: 0, foreign: 0 };
      data.value.feedCount =
        keyword_counts && keyword_counts[feedType]
          ? keyword_counts[feedType]
          : 0;
    } else {
      data.value.feedCount = data.value.articles.length;
    }
    data.value.feedCount -= removedArticles.items.length;
    data.value.remainingArticles =
      data.value.feedCount - data.value.articles.length;
    data.value.isViewMoreButtonVisible =
      hasViewMoreButton.value && data.value.remainingArticles > 0;
    data.value.isFetchingData = false;
    data.value.isLoaded = true;
  };

  return {
    articles: [] as Article[],
    feedCount: 0,
    isLoaded: false,
    isFetchingData: false,
    isViewMoreButtonVisible: false,
    remainingArticles: 0,
    load: load,
    loadMore: async () => {
      const data = articlesByDate.find(d => d.date === date)?.[feedType];
      if (data?.value) {
        // フィードは200件まで作成されている
        await load(Math.min(data.value.feedCount, 200));
      }
    },
  };
};

const emptyFeedData = () => ({
  articles: [] as Article[],
  feedCount: 0,
  isLoaded: true,
  isFetchingData: false,
  isViewMoreButtonVisible: false,
  remainingArticles: 0,
  load: () => undefined,
  loadMore: () => undefined,
});

const articlesByDate: DateArticles[] = shallowReactive([]);
const pastArticlesDate = ref('');
const setArticlesForDate = (feedLastUpdatedAt: Dayjs, dayNumber: number) => {
  const date = feedLastUpdatedAt.subtract(dayNumber, 'days');

  const data: DateArticles = {
    date: date,
    dateStr: date.format('YYYY-MM-DD'),
    dateLabel: formatDateForFeedLabel(date, feedLastUpdatedAt),
    domestic: ref(
      ['all', 'domestic'].includes(feedType.value)
        ? getFeedData('domestic', date)
        : emptyFeedData(),
    ),
    foreign: ref(
      ['all', 'foreign'].includes(feedType.value) &&
        theme.value?.is_foreign_media_enabled
        ? getFeedData('foreign', date)
        : emptyFeedData(),
    ),
  };
  articlesByDate[dayNumber] = data;
};

let feedObserver: IntersectionObserver | undefined = undefined;
const feedRefs = ref<HTMLElement[] | undefined>();
const initializeFeedsObserver = () => {
  feedObserver?.disconnect();
  // スクロールしたら次の日のフィードが画面に表示する時にデータを取得する
  feedObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0) {
          const dateStr = (entry.target as HTMLElement).dataset.dateStr;
          const date = dayjs(dateStr);
          const data = articlesByDate
            .slice(1)
            .find(d => d.date.isSame(date, 'day'));
          if (
            data &&
            data.domestic.value.articles.length === 0 &&
            data.foreign.value.articles.length === 0
          ) {
            data.domestic.value.load();
            data.foreign.value.load();
          }

          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '120px 0px 0px 0px' },
  );
  feedRefs.value?.forEach((el, i) => {
    if (i === 0) return;
    feedObserver?.observe(el);
  });
};

const initialize = async () => {
  isLoadingInitial.value = true;
  feedObserver?.disconnect();
  articlesByDate.splice(0);
  if (!theme.value?.id || !hasFeed.value) {
    isLoadingInitial.value = false;
    return;
  }

  let dateCount = 7; // デフォルト7日間表示
  // キーワードフィードの場合はフィード作成日まで表示。その下に過去記事表示が出る
  if (feed.value === 'keyword_feed' || feed.value === 'related_feed') {
    const sortedDates = Object.keys(
      theme.value?.keyword_feed_counts ?? [],
    ).sort();
    if (sortedDates.length > 0) {
      const initialFeedDate = dayjs(sortedDates[0]);
      dateCount = Math.min(
        7,
        feedLastUpdatedAt.value.diff(dayjs(initialFeedDate), 'day') + 1,
      );
    } else {
      dateCount = 1;
    }
  }
  if (dateCount > 0) {
    for (let i = 0; i < dateCount; i++) {
      setArticlesForDate(feedLastUpdatedAt.value, i);
    }
    pastArticlesDate.value = formatDateForFeedLabel(
      feedLastUpdatedAt.value.subtract(dateCount, 'days'),
      feedLastUpdatedAt.value,
    );

    await Promise.all([
      articlesByDate[0].domestic.value.load(),
      articlesByDate[0].foreign.value.load(),
    ]);

    currentDayFeedCount.value =
      articlesByDate[0].domestic.value.feedCount +
      articlesByDate[0].foreign.value.feedCount;
  }
  feedDayCount.value = dateCount;

  isLoadingInitial.value = false;

  await nextTick();

  initializeFeedsObserver();
  initializePastArticlesObserver();
};

watch(
  () => theme.value?.id,
  async () => await initialize(),
);
watch(
  () => feedType.value,
  async () => {
    removedArticles.items = [];
    await mutateTheme();
    await initialize();
  },
);

onMounted(async () => {
  await initialize();
  emitter.on('article-updated', updateArticle);
  emitter.on('article-viewed', updateArticle);
  emitter.on('article-removed', removeArticle);
  emitter.on('personal-feed-article-removed', removePersonalNewsArticle);
  emitter.on('personal-feed-article-restored', restorePersonalNewsArticle);
});

onUnmounted(() => {
  emitter.off('article-updated', updateArticle);
  emitter.off('article-viewed', updateArticle);
  emitter.off('article-removed', removeArticle);
  emitter.off('personal-feed-article-removed', removePersonalNewsArticle);
  emitter.off('personal-feed-article-restored', restorePersonalNewsArticle);
  feedObserver?.disconnect();
  pastArticlesObserver?.disconnect();
});

const updateArticle = (updatedArticle: AdpDocument) => {
  articlesByDate.forEach(data => {
    [data.domestic.value.articles, data.foreign.value.articles].forEach(
      articles => syncAdpDocumentActions(articles, updatedArticle),
    );
  });
};

const removedArticles = reactive({ items: [] as number[] });

const removeArticle = ({ articleId }: { articleId: number }) => {
  removedArticles.items.push(articleId);
  updateFeedCounts(articleId, -1);
};
const restoreArticle = ({ articleId }: { articleId: number }) => {
  const removed = [...removedArticles.items];
  if (removed.includes(articleId)) {
    removedArticles.items = removed.filter(a => a !== articleId);
    updateFeedCounts(articleId, 1);
  }
};
const updateFeedCounts = (articleId: number, amount: number) => {
  articlesByDate.forEach((data, i) => {
    const inDomestic = data.domestic.value.articles.find(
      a => a.id === articleId,
    );
    const inForeign = data.foreign.value.articles.find(a => a.id === articleId);

    if (inDomestic) {
      data.domestic.value.feedCount += amount;
    }
    if (inForeign) {
      data.foreign.value.feedCount += amount;
    }
    if ((inDomestic || inForeign) && i === 0) {
      currentDayFeedCount.value += amount;
    }
  });
};

const removePersonalNewsArticle = (article: Article) =>
  removeArticle({ articleId: article.id });
const restorePersonalNewsArticle = (article: Article) => {
  restoreArticle({ articleId: article.id });
};

const articleCountBeforeDate = (
  date: Dayjs,
  feedType: 'domestic' | 'foreign',
) =>
  articlesByDate
    .filter(d => d.date.isAfter(date))
    .reduce((sum, d) => sum + d[feedType].value.articles.length, 0);

const loadToDate = async (date?: Dayjs) => {
  const notLoadedIndicies = articlesByDate
    .filter(
      d =>
        (date === undefined || d.date.isAfter(date, 'day')) &&
        (!d.domestic.value.isLoaded || !d.foreign.value.isLoaded),
    )
    .map(d => articlesByDate.indexOf(d));
  for (let d of notLoadedIndicies) {
    await Promise.all([
      articlesByDate[d].domestic.value.load(),
      articlesByDate[d].foreign.value.load(),
    ]);
  }
};

const dateSelectRefs = ref<HTMLElement[] | undefined>();

const selectDate = async (dateStr: string) => {
  let targetElement: HTMLElement | undefined;
  let loadDate: Dayjs | undefined = undefined;

  // 過去記事
  if (dateStr === SCROLL_TO_PAST_ARTICLES) {
    targetElement = pastArticlesRef.value ?? undefined;

    // 過去記事部分をスクロール済み状態にしておく
    scrolledPastArticles.value = true;
  }
  // 毎日の記事
  else {
    targetElement = dateSelectRefs.value?.find(
      r => r.dataset.dateStr === dateStr,
    );
    loadDate = dayjs(dateStr);
  }
  if (!targetElement) return;

  await loadToDate(loadDate);
  await nextTick();
  // NOTE: スクロール位置はscroll-margin-topで調整している
  targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const SCROLL_TO_PAST_ARTICLES = 'scroll-to-past-articles' as const;
const pastArticlesValue = ref<string>(SCROLL_TO_PAST_ARTICLES);

const dateOptions = computed(() => [
  ...articlesByDate.map(d => ({ label: d.dateLabel, value: d.dateStr })),
  ...(showPastArticles.value
    ? [
        {
          label: `${pastArticlesDate.value}までのニュース`,
          value: SCROLL_TO_PAST_ARTICLES,
        },
      ]
    : []),
]);

const selectDateFromPastArticles = async (value: string | undefined) => {
  if (!value || value === SCROLL_TO_PAST_ARTICLES) return;
  await selectDate(value);

  // セレクトボックスの表示を強制的に戻すために、
  // 選択された値を代入してから元の値を入れ直して変更検知を走らせる
  pastArticlesValue.value = value;
  nextTick(() => {
    pastArticlesValue.value = SCROLL_TO_PAST_ARTICLES;
  });
};

const pastArticlesRef = useTemplateRef('past-articles');
const scrolledPastArticles = ref(false);
let pastArticlesObserver: IntersectionObserver | undefined = undefined;
const initializePastArticlesObserver = () => {
  pastArticlesObserver?.disconnect();
  pastArticlesObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.intersectionRatio > 0) {
        scrolledPastArticles.value = true;
        observer.unobserve(entry.target);
      }
    });
  });
  if (pastArticlesRef.value) {
    pastArticlesObserver.observe(pastArticlesRef.value);
  }
};

const isInitialStateKeywordFeed = computed(
  () =>
    feed.value === 'keyword_feed' &&
    (theme.value?.feed_last_updated_at ?? null) === null,
);

const pastArticlesToDate = computed(() => {
  if ((theme.value?.feed_last_updated_at ?? null) === null) {
    return dayjs(theme.value?.created_at).subtract(1, 'day').endOf('day');
  } else {
    return dayjs().subtract(7, 'day').endOf('day');
  }
});

const keywordFeedCount = (feedType: Exclude<FeedType, 'all'>) => {
  const lengthList = articlesByDate.map(d => d[feedType].value.articles.length);
  return lengthList.length > 0 ? lengthList.reduce((sum, n) => sum + n) : 0;
};

const markSource: ArticleMarkSource = 'theme';
const feature: Feature = feed.value;

const showPastArticles = computed(() => {
  return (
    theme.value &&
    feed.value === 'keyword_feed' &&
    !theme.value.is_chinese &&
    (feedType.value !== 'foreign' || theme.value?.is_foreign_media_enabled)
  );
});
</script>
<template>
  <div class="feed-articles-container">
    <div
      v-if="hasFeed && !isLoadingInitial && !isInitialStateKeywordFeed"
      class="feed-articles"
    >
      <div
        v-for="data in articlesByDate"
        ref="feedRefs"
        :data-date-str="data.dateStr"
        :key="data.dateLabel"
      >
        <div ref="dateSelectRefs" :data-date-str="data.dateStr">
          <FeedArticlesDay
            v-if="theme"
            :theme
            :feed
            :feed-type
            :data
            :is-first="data.dateStr === articlesByDate[0]?.dateStr"
            :date-options="dateOptions"
            :domestic-article-count-before-date="
              articleCountBeforeDate(data.date, 'domestic')
            "
            :foreign-article-count-before-date="
              articleCountBeforeDate(data.date, 'foreign')
            "
            :removed-article-ids="removedArticles.items"
            @select-date="selectDate"
          />
        </div>
      </div>
    </div>
    <div v-else-if="isLoadingInitial" class="loading-container">
      <DgrLoading class="loading" />
    </div>
    <template v-else-if="theme">
      <ThemeEmptyFeed
        v-if="!hasFeed"
        class="empty-feed"
        :theme="theme"
        :display-feed-type="feedType"
        :theme-feed-type="feedType"
        :enable-to-edit-theme="enableToEditTheme"
        doc-type="article"
        >{{ EMPTY_KEYWORD_FEED_MESSAGE }}</ThemeEmptyFeed
      >
      <ThemeEmptyState
        v-if="isInitialStateKeywordFeed"
        :is-chinese="theme.is_chinese"
      />
    </template>

    <div
      v-if="theme && showPastArticles && !isLoadingInitial"
      ref="past-articles"
      class="past-articles"
    >
      <div class="past-articles-container">
        <div class="date-header">
          <FeedDateSelectbox
            :options="dateOptions"
            :model-value="pastArticlesValue"
            @update:model-value="selectDateFromPastArticles"
          />
        </div>
        <hr class="header-divider" />
        <div
          v-if="isInitialStateKeywordFeed || scrolledPastArticles"
          class="past-articles-content"
        >
          <div
            v-if="feedType === 'all' || feedType === 'domestic'"
            class="media-section"
          >
            <div class="media-label c-title-bold--m">国内メディア</div>
            <div class="divider"></div>
            <PastArticles
              :keywords="theme ? theme.keywords : []"
              :to_date="pastArticlesToDate"
              lang="ja"
              :rank-offset="keywordFeedCount('domestic') + 1"
              :theme="theme"
              :page-name="feed"
              :mark-source="markSource"
              :feature="feature"
            />
          </div>
          <div
            v-if="feedType === 'all' || feedType === 'foreign'"
            class="media-section"
          >
            <div class="media-label c-title-bold--m">海外メディア</div>
            <div class="divider"></div>
            <PastArticles
              v-if="theme.is_foreign_media_enabled"
              :keywords="theme ? theme.keywords : []"
              :to_date="pastArticlesToDate"
              lang="en"
              :rank-offset="keywordFeedCount('foreign') + 1"
              :theme="theme"
              :page-name="feed"
              :mark-source="markSource"
              :feature="feature"
            />
            <ThemeEmptyFeed
              v-else
              :display-feed-type="feedType"
              theme-feed-type="foreign"
              doc-type="article"
              :theme="theme"
              :enable-to-edit-theme="enableToEditTheme"
            />
          </div>
        </div>
      </div>
      <div v-if="scrolledPastArticles" class="induction">
        <div class="image-wrap">
          <img class="image" src="@/assets/login_visual.jpg" />
        </div>
        <div class="spacing-12" />
        <div class="message c-title-bold--xm">
          気になる記事は見つかりましたか？
        </div>
        <div class="spacing-12" />
        <div class="message c-text-regular--m">
          あなたの興味・関心にあった記事が少ないなと感じたら、<br />
          キーワードを追加してみてください。
        </div>
        <div class="spacing-12" />
        <div class="message c-text-regular--m">
          <router-link
            class="green-600 link"
            :to="{ path: `/themes/${theme.id}/edit` }"
          >
            キーワードを追加する
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.feed-articles-container {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 616px;

  .title {
    display: flex;
    align-items: center;
    height: 24px;
    margin-bottom: 16px;

    .count {
      color: $color-gray600;
    }
  }

  .divider {
    width: auto;
    border-bottom: 0.5px solid $color-gray400;
    margin: 0px 16px 0px 16px;
  }

  .feed-articles {
    display: flex;
    flex-direction: column;
  }

  .date-divider {
    position: relative;
    top: 16px;
    width: 616px;
    height: 1px;
    background: $color-gray400;
  }

  .date-select {
    position: sticky;
    top: var(--layout-sticky-top-under-tabs, 112px);
    z-index: var(--z-dropdown);
    display: flex;
    justify-content: center;
  }

  .article-card {
    margin-bottom: 8px;
  }

  .view-more-button {
    display: inline-flex;
    align-self: center;
    padding: 4px 12px;
    border-radius: 4px;
    border: 1px solid $color-green600;
    background: $color-white;
    color: $color-green600;
    cursor: pointer;
  }

  .loading {
    align-self: center;
  }
  .loading-spacer {
    padding-bottom: 80vh;
  }

  .domestic-title {
    margin-bottom: 12px;
  }

  .date-select-box {
    :deep(.c-selectBox) {
      border: none;
    }
  }

  .loading-container {
    width: 616px;
    display: flex;
    flex-direction: column;
  }

  .green-600 {
    color: $color-green600;
  }

  .induction {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: $color-white;
    padding: 24px 16px;
    margin-top: 28px;
    border: 1px solid $color-gray400;
    border-radius: 4px;
    box-sizing: border-box;

    .image-wrap {
      display: flex;
      justify-content: center;
      .image {
        width: 120px;
        height: 120px;
      }
    }

    .message {
      text-align: center;
    }
    .link {
      &:hover {
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }

  .past-articles-content {
    background-color: $color-white;
    border-radius: 4px;
    margin-bottom: 16px;
  }

  .past-articles-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
    border-radius: 4px;
    background: $color-white;

    .date-header {
      position: sticky;
      top: var(--layout-sticky-top-under-tabs, 112px);
      z-index: 1;

      display: flex;
      align-items: center;
      height: 64px;
      padding: 0 16px;
      background: $color-white;
      border-radius: 4px;
    }

    .header-divider {
      border: none;
      border-top: 1px solid $color-gray200;
      padding-bottom: 16px;
      margin: 0;
    }

    .past-articles-content {
      background-color: $color-white;
      border-radius: 4px;
      margin-bottom: 0;
    }
  }

  .media-label {
    display: flex;
    align-items: center;
    background: $color-white;
    height: 40px;
    padding-left: 16px;
    &.c-title-bold--m {
      border-radius: 4px;
    }
    &.sticky {
      position: sticky;
      top: var(--layout-sticky-top-under-tabs, 112px);
      z-index: 1;
    }
  }

  .empty-feed {
    margin-bottom: 16px;
  }

  [data-date-str],
  .past-articles {
    scroll-margin-top: var(--layout-sticky-top-under-tabs, 112px);
  }
}
</style>
